import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { CheckoutActions } from '@app/pages/checkout-app/checkout-store/actions';
import { onDestroyComplete } from '@app/sharedModule/decorators/on-destroy-unsubscribe.decorator';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { ExtensionComponentType, ICheckoutExtensions } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { DynamicRule } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getExtensionRules } from '@pages/checkout-app/checkout-layout/main/components/extensions/shared/all-extension-rules';
import { TypedRuleGroup } from '@app/sharedModule/components/rules-dynamic-form/model';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { IDynamicExtension, isDynamicExtension } from '../../../models';
import { DynamicExtensionFormService } from './dynamic-extension-form.service';
import {
  DynamicControlType,
  DynamicFieldDescriptor,
  DynamicSettingsFormGroup,
  SettingsJsonSchema,
} from './dynamic-extension.model';

@Component({
  selector: 'dynamic-extension',
  templateUrl: './dynamic-extension.component.html',
  styleUrls: ['./dynamic-extension.component.scss'],
  standalone: false,
})
export class DynamicExtensionComponent implements OnInit, OnDestroy {
  @onDestroyComplete() destroy$ = new Subject();
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  formGroup: DynamicSettingsFormGroup = new FormGroup<Record<string, FormControl>>({});
  conditionsFormGroup = new FormGroup<{ restrictionsRules: FormArray<FormGroup<TypedRuleGroup>> }>({
    restrictionsRules: new FormArray<FormGroup<TypedRuleGroup>>([]),
  });
  usageTokenControl = new FormControl<string>('', { nonNullable: true });
  fields: DynamicFieldDescriptor[] = [];
  displayRules = getExtensionRules();
  displayRulesEntities = this.displayRules.map((rule) => rule.entity);
  readonly controlType = DynamicControlType;
  initialized = false;
  hasEditableFields = false;
  private loadedExtension: IDynamicExtension | null = null;
  private originalSettingsValues: Record<string, unknown> = {};
  private statusChangesSub?: Subscription;

  constructor(
    private store: Store<CheckoutReducer.State>,
    private extensionsService: ExtensionsService,
    private formService: DynamicExtensionFormService,
    private rulesDynamicFormService: RulesDynamicFormService,
  ) {}

  ngOnDestroy() {}

  ngOnInit() {
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.DYNAMIC_EXTENSION);
    this.extensionsService.save$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.handleSave();
    });

    this.conditionsFormGroup.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateFormValidity();
    });

    this.loadedExtension$.pipe(takeUntil(this.destroy$)).subscribe((extension: ICheckoutExtensions) => {
      // Promoted/APPROVED extensions are served by their own generated component under a distinct
      // ExtensionComponentType, so this generic renderer only handles rows still typed DYNAMIC_EXTENSION.
      if (extension && isDynamicExtension(extension)) {
        this.loadedExtension = extension;
        this.initForm(extension);
      }
    });
  }

  handleSave() {
    if (!this.loadedExtension?.id) return;
    if (this.formGroup.invalid || this.conditionsFormGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.conditionsFormGroup.markAllAsTouched();
      this.extensionsService.setFormValid(false);
      return;
    }
    const settingsValues = { ...this.originalSettingsValues, ...this.formGroup.getRawValue() };
    const restrictionsRules = this.restrictionsRulesFormArray.getRawValue() as DynamicRule[];
    const template = { ...(this.loadedExtension.template ?? {}), settingsValues, restrictionsRules };
    const payload: Partial<ICheckoutExtensions> = {
      template,
      usageToken: this.loadedExtension.usageToken,
      type: ExtensionComponentType.DYNAMIC_EXTENSION,
    };
    this.store.dispatch(CheckoutActions.updateExtension({ id: this.loadedExtension.id, extension: payload }));
  }

  private get restrictionsRulesFormArray(): FormArray<FormGroup<TypedRuleGroup>> {
    return this.conditionsFormGroup.controls.restrictionsRules;
  }

  private initForm(extension: IDynamicExtension): void {
    const template = extension.template ?? {};
    const schema = (template.settingsSchema ?? null) as SettingsJsonSchema | null;
    this.originalSettingsValues = (template.settingsValues ?? {}) as Record<string, unknown>;

    const { formGroup, fields } = this.formService.buildForm(schema, this.originalSettingsValues);
    this.formGroup = formGroup;
    this.fields = fields;
    this.hasEditableFields = Object.keys(formGroup.controls).length > 0;

    this.usageTokenControl.setValue(extension.usageToken ?? '');
    this.conditionsFormGroup.setControl(
      'restrictionsRules',
      this.rulesDynamicFormService.initRulesFormArray(template.restrictionsRules ?? [], this.displayRules) as FormArray<
        FormGroup<TypedRuleGroup>
      >,
    );

    this.initialized = true;

    this.statusChangesSub?.unsubscribe();
    this.updateFormValidity();
    this.statusChangesSub = this.formGroup.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateFormValidity();
    });
  }

  private updateFormValidity(): void {
    this.extensionsService.setFormValid(this.formGroup.valid && this.conditionsFormGroup.valid);
  }
}
